// Timer zones of one map, and the small JSON file players can export and share.
// Coordinates are Source units (the numbers you'd see with getpos in the game).

import type { Zone } from '../sim/zones';

export interface MapZones {
  map: string;
  start: Zone | null;
  end: Zone | null;
  checkpoints: Zone[];
  updatedAt?: number;
  /** Where the zones came from when the player didn't draw them (e.g. "zones-cstrike"). */
  source?: string;
}

export const ZONE_FILE_FORMAT = 'bhop-web-zones';

export function emptyZones(map: string): MapZones {
  return { map, start: null, end: null, checkpoints: [] };
}

export function zonesToJson(z: MapZones): string {
  const round = (v: number) => Math.round(v * 100) / 100;
  const box = (b: Zone) => ({ min: b.min.map(round), max: b.max.map(round) });
  return JSON.stringify(
    {
      format: ZONE_FILE_FORMAT,
      version: 1,
      map: z.map,
      start: z.start && box(z.start),
      end: z.end && box(z.end),
      checkpoints: z.checkpoints.map(box),
    },
    null,
    2,
  );
}

function parseBox(v: unknown): Zone | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as { min?: unknown; max?: unknown };
  const vec = (a: unknown) =>
    Array.isArray(a) && a.length === 3 && a.every((n) => typeof n === 'number' && Number.isFinite(n))
      ? ([a[0], a[1], a[2]] as [number, number, number])
      : null;
  const min = vec(o.min);
  const max = vec(o.max);
  if (!min || !max) return null;
  // Accept corners in any order.
  return {
    min: [Math.min(min[0], max[0]), Math.min(min[1], max[1]), Math.min(min[2], max[2])],
    max: [Math.max(min[0], max[0]), Math.max(min[1], max[1]), Math.max(min[2], max[2])],
  };
}

/** Parse an exported zone file. Throws with a readable message if it isn't one. */
export function zonesFromJson(text: string): MapZones {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error("That file isn't valid JSON.");
  }
  const o = raw as Record<string, unknown>;
  if (!o || o.format !== ZONE_FILE_FORMAT) throw new Error("That isn't a bhop-web zone file.");
  const checkpoints = Array.isArray(o.checkpoints)
    ? o.checkpoints.map(parseBox).filter((b): b is Zone => !!b)
    : [];
  return {
    map: typeof o.map === 'string' ? o.map : '',
    start: parseBox(o.start),
    end: parseBox(o.end),
    checkpoints,
  };
}

/** Zone from two corners the player stood at (origins at the feet). Covers the player's width and a bit of height. */
export function zoneFromCorners(a: readonly number[], b: readonly number[]): Zone {
  const pad = 16;
  return {
    min: [Math.min(a[0], b[0]) - pad, Math.min(a[1], b[1]) - pad, Math.min(a[2], b[2]) - 2],
    max: [Math.max(a[0], b[0]) + pad, Math.max(a[1], b[1]) + pad, Math.max(a[2], b[2]) + 128],
  };
}
